import { postTimeseriesHmlReport } from "lib/api/postTimeseriesHmlReport";
import { useAppStore } from "lib/store/useAppStore";
import { useTranslation } from "react-i18next";

function getDisplayText(value, fallback = "-") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function getChartBase64(chartRef) {
  try {
    const chart = chartRef?.current;
    const sourceCanvas = chart?.canvas;

    if (!sourceCanvas) {
      return chart?.toBase64Image?.() || "";
    }

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = sourceCanvas.width;
    exportCanvas.height = sourceCanvas.height;

    const context = exportCanvas.getContext("2d");
    if (!context) {
      return chart?.toBase64Image?.() || "";
    }

    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    context.drawImage(sourceCanvas, 0, 0);

    return exportCanvas.toDataURL("image/png");
  } catch (error) {
    console.warn("[TimeseriesHmlDownloadBtn] chart capture failed", error);
    return "";
  }
}

function getTimeseriesDownloadFileName(file = {}) {
  const rawName =
    file?.file_clean_file_name ||
    file?.clean_file_name ||
    file?.file_name ||
    file?.upload_id ||
    "timeseries_report";
  const extIndex = rawName.lastIndexOf(".");
  return extIndex > 0 ? rawName.slice(0, extIndex) : rawName;
}

export default function TimeseriesHmlDownloadBtn({
  file,
  selectedFiles,
  selectedImageTypeLabel,
  imageType,
  representativeUploadIdSet,
  ultrasonicChartRef,
  temperatureChartRef,
  averageUltrasonic,
  averageTemperature,
  averageThreePhaseDiffValue,
  comprehensiveOpinion,
}) {
  const { t } = useTranslation();
  const showAlert = useAppStore((s) => s.showAlert);
  const representativeCount = representativeUploadIdSet?.size ?? 0;
  const canDownload = representativeCount >= 2 && representativeCount <= 3;
  const representativeGuideMessage = t(
    "imageFileIndex.timeseries.messages.selectTwoOrThreeRepresentatives",
    {
      defaultValue:
        "한글 보고서를 다운로드하려면 대표 이미지를 2~3개 선택하세요.",
    },
  );

  const handleDownload = async () => {
    if (!canDownload) {
      showAlert({
        text: representativeGuideMessage,
        success: false,
        autoClose: true,
      });
      return;
    }

    const sortedFiles = Array.isArray(selectedFiles) ? selectedFiles : [];
    const representativeFiles = sortedFiles
      .filter((item) =>
        representativeUploadIdSet?.has(String(item?.upload_id ?? "")),
      )
      .slice(0, 3);

    if (representativeFiles.length < 2 || representativeFiles.length > 3) {
      showAlert({
        text: representativeGuideMessage,
        success: false,
        autoClose: true,
      });
      return;
    }

    const periodStart = sortedFiles[0];
    const periodEnd = sortedFiles[sortedFiles.length - 1];
    const payload = {
      source_upload_id: String(file?.upload_id ?? ""),
      selected_upload_ids: sortedFiles.map((item) =>
        String(item?.upload_id ?? ""),
      ),
      representative_upload_ids: representativeFiles.map((item) =>
        String(item?.upload_id ?? ""),
      ),
      image_type: imageType,
      image_type_label: selectedImageTypeLabel,
      analysis_period: `${getDisplayText(periodStart?.shooting_date)} ~ ${getDisplayText(periodEnd?.shooting_date)}`,
      total_data_count: sortedFiles.length,
      average_ultrasound: averageUltrasonic,
      average_thermal: averageTemperature,
      average_triplespot_dif: averageThreePhaseDiffValue,
      timeseries_inspection_result: String(comprehensiveOpinion ?? "").trim(),
      ultrasound_trend_graph: getChartBase64(ultrasonicChartRef),
      thermal_trend_graph: getChartBase64(temperatureChartRef),
    };

    const result = await postTimeseriesHmlReport(
      file?.upload_id,
      getTimeseriesDownloadFileName(file),
      payload,
    );

    if (!result?.success) {
      showAlert({
        text: result.message || "Failed to download timeseries HML report.",
        messageKey: result.messageKey,
        messageParams: result.messageParams,
        success: false,
        autoClose: true,
      });
    }
  };

  return (
    <button
      type="button"
      className="btn-bg-blue"
      onClick={handleDownload}
      title={!canDownload ? representativeGuideMessage : undefined}
    >
      {t("imageFileIndex.buttons.HmlDownload")}
    </button>
  );
}
